/**
 * Baris tambahan kartu "Perlu perhatian Anda" di Beranda (#1758) — MURNI, tanpa React.
 *
 * Keputusan Owner 24 Okt 2026: Beranda Owner/Manajer menjawab "apa yang perlu saya putuskan hari
 * ini". Kartu ini sebelumnya hanya memuat pengajuan koreksi dan tugas yang baru selesai.
 *
 * Yang ditambahkan HANYA hal yang punya KEADAAN — angka yang turun sendiri begitu ditangani:
 * - **Tugas draf** — turun begitu ditugaskan.
 * - **Alert alat terbuka** — turun begitu alert-nya ditutup.
 *
 * "Tugas gagal" SENGAJA tidak masuk, walaupun ada di usulan awal: hitungan `failed` dari ringkasan
 * tugas adalah jumlah sepanjang masa dan tidak pernah turun. Kartu yang selalu menyala persis sama
 * tidak berartinya dengan kartu yang tidak pernah menyala (alasan yang sama dengan
 * `pemberitahuan.ts`). Angka gagal tetap terlihat di papan Ringkasan Beranda.
 */

import { dapatMengelolaTugas } from './bel-pemberitahuan';
import { hasAnyPermission } from './navigation';
import { isOversightRole, type WorkforceRole } from './worker-roles';

/**
 * Siapa yang mendapat baris draf & alert — HANYA Owner/Manajer (pengawas).
 *
 * Keputusan Owner #1758 poin 3 menyebut "Beranda Owner/Manajer". Kartu ini dirender di Beranda
 * SEMUA peran, dan Teknisi memegang izin alert (`telemetry.read` + `asset.read`) — tanpa gerbang
 * peran ini, Beranda Teknisi ikut berubah: kartu muncul di atas tombol Absen besarnya, dengan
 * tautan "Lihat semua" ke halaman Pemberitahuan yang baginya selalu kosong (temuan review
 * independen R1). Izin tetap disyaratkan juga: pengawas tanpa izin layarnya tidak diberi tautan
 * ke layar yang menolaknya.
 */
export function syaratBarisLain(
  roles: readonly string[],
  permissions: readonly string[],
): { readonly draf: boolean; readonly alert: boolean } {
  const pengawas = isOversightRole(roles as readonly WorkforceRole[]);
  return {
    draf: pengawas && dapatMengelolaTugas(permissions),
    alert:
      pengawas &&
      hasAnyPermission(permissions, ['workforce.telemetry.read']) &&
      hasAnyPermission(permissions, ['workforce.asset.read']),
  };
}

/**
 * Batas atas daftar alert dari server (`alert-event.repository.ts`, bawaan 100). Daftar sepanjang
 * ini berarti mungkin ada lebih banyak — angkanya ditulis "100+", bukan angka pasti yang keliru.
 */
export const BATAS_DAFTAR_ALERT = 100;

export interface BarisPerhatian {
  readonly kunci: 'draf' | 'alert';
  readonly teks: string;
  readonly to: string;
}

export function barisPerhatianLain(input: {
  /** `null` = tidak boleh dilihat atau gagal dimuat — baris tidak ditampilkan. */
  readonly tugasDraf: number | null;
  readonly alertTerbuka: number | null;
}): readonly BarisPerhatian[] {
  const baris: BarisPerhatian[] = [];
  if (input.tugasDraf !== null && input.tugasDraf > 0) {
    baris.push({
      kunci: 'draf',
      teks: `${input.tugasDraf} tugas draf belum ditugaskan`,
      to: '/tasks/manage?status=draft',
    });
  }
  if (input.alertTerbuka !== null && input.alertTerbuka > 0) {
    baris.push({
      kunci: 'alert',
      teks: `${input.alertTerbuka >= BATAS_DAFTAR_ALERT ? `${BATAS_DAFTAR_ALERT}+` : input.alertTerbuka} alert alat belum ditutup`,
      to: '/alerts',
    });
  }
  return baris;
}
